import { forwardRef, useImperativeHandle, useRef } from "react";
import { useParams } from "react-router-dom";

import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";
import ComplexFieldCreate from "../ComplexField/ComplexFieldCreate";
import CurrencyInterval from "../../Inputs/CurrencyInterval";
import ReferenceCreate from "../References/ReferenceCreate";
import { Module } from "../../../types/TenantInterfaces";
import LocationCreate from "../Location/LocationCreate";
import DateTime from "../../Inputs/DateTime/DateTime";
import ToggleCreate from "../../Inputs/ToggleCreate";
import SingleSelect from "../Selects/SingleSelect";
import CalculatedCreate from "../CalculatedCreate";
import FilterCreate from "../Filter/FilterCreate";
import Constants from "../../../utils/Constants";
import MultiSelect from "../Selects/MultiSelect";
import SingleFile from "../../Inputs/SingleFile";
import ColorCreate from "../Color/ColorCreate";
import RichTextCreate from "../RichTextCreate";
import Currency from "../../Inputs/Currency";
import ObjectCreate from "../ObjectCreate";
import MediaCreate from "../MediaCreate";
import Text from "../../Inputs/Text";
import TagCreate from "../TagCreate";

interface InputFormatterProps {
  module: Module;
  field: any;
  value?: any;
  size?: "sm" | "lg";
  onChange?: (field: any, value: any) => void;
  inline?: boolean;
  useMaxZIndex?: boolean;
  disabled?: boolean;
  filter?: boolean;
  placeholder?: string;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

// Returns Input component based on provided field (uses field.FieldType)
const InputFormatter = forwardRef<HTMLInputElement, InputFormatterProps>(
  (
    {
      module,
      field,
      value = "", // default value
      size = "sm",
      onChange = () => {},
      inline = false,
      disabled = false, // Whether or not the input is disabled
      filter = false, // Used for Filter Create
      placeholder = "", // Used for placeholder on some fields
      reusableViewReferenceFields,
    }: InputFormatterProps,
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { pageID } = useParams();

    // @ts-ignore
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef?.current?.focus();
      },
    }));

    if (!field || !field?.FieldType) return value;

    let type = field.FieldType;

    if (type === Constants.FIELDTYPES.IMAGE) {
      return (
        <MediaCreate
          field={field}
          existingValue={value}
          type={type}
          onChange={(val) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.VIDEO) {
      return (
        <MediaCreate
          field={field}
          existingValue={value}
          type={type}
          onChange={(val) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.AUDIO) {
      return (
        <MediaCreate
          field={field}
          existingValue={value}
          type={type}
          onChange={(val) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (
      type === Constants.FIELDTYPES.EMAIL ||
      type === Constants.FIELDTYPES.URL
    ) {
      return (
        <ComplexFieldCreate
          type={type}
          field={field}
          inputSize={size}
          onChange={(val) => onChange(field, val)}
          existingValue={value}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (
      type === Constants.FIELDTYPES.TEXT ||
      type === Constants.FIELDTYPES.LONG_TEXT ||
      type === Constants.FIELDTYPES.NUMBER ||
      type === Constants.FIELDTYPES.PERCENT
    ) {
      return (
        <Text
          type={type}
          field={field}
          inputSize={size}
          onChange={(val) => onChange(field, val)}
          disabled={disabled}
          existingValue={value}
          placeholder={placeholder}
          autoFocus={false}
        />
      );
    }

    if (
      type === Constants.FIELDTYPES.DATE ||
      type === Constants.FIELDTYPES.DATE_TIME ||
      type === Constants.FIELDTYPES.TIME
    ) {
      return (
        <>
          <DateTime
            type={type}
            field={field}
            existingValue={value}
            inputSize={size}
            onChange={(val: any) => onChange(field, val)}
            disabled={disabled}
          />
        </>
      );
    }

    if (type === Constants.FIELDTYPES.PHONE) {
      return (
        <ComplexFieldCreate
          type={type}
          field={field}
          inputSize={size}
          onChange={(val) => onChange(field, val)}
          existingValue={value}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.SINGLE_REFERENCE) {
      return (
        <ReferenceCreate
          // @ts-ignore
          field={field}
          value={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          inline={inline}
          disabled={disabled}
          ref={inputRef}
          pageID={pageID}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
      );
    }

    if (type === Constants.FIELDTYPES.MULTI_REFERENCE) {
      return (
        <ReferenceCreate
          ref={inputRef}
          // @ts-ignore
          field={field}
          value={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          inline={inline}
          disabled={disabled}
          pageID={pageID}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
      );
    }

    if (type === Constants.FIELDTYPES.SINGLE_SELECT) {
      return (
        <SingleSelect
          field={field}
          value={value}
          onChange={(val: any) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.MULTI_SELECT) {
      return (
        <MultiSelect
          field={field}
          value={value}
          onChange={(val: any) => onChange(field, val)}
          disabled={disabled}
          modifyTrigger="click"
          placeholder={undefined}
          useMaxZIndex={undefined}
          table={undefined}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.TOGGLE) {
      return (
        <ToggleCreate
          type={type}
          field={field}
          value={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          inline={inline}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.CURRENCY) {
      return (
        <Currency
          field={field}
          existingValue={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          disabled={disabled}
        />
      );
    }

    if (type === Constants.FIELDTYPES.CURRENCY_INTERVAL) {
      return (
        <CurrencyInterval
          field={field}
          existingValue={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          disabled={disabled}
        />
      );
    }
    if (type === Constants.FIELDTYPES.SINGLE_FILE) {
      return (
        <SingleFile
          type={type}
          field={field}
          value={value}
          inputSize={size}
          onChange={(val) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.RICH_TEXT) {
      return (
        <RichTextCreate
          defaultValue={value}
          field={field}
          onChange={onChange}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.OBJECT) {
      return (
        <ObjectCreate
          value={value}
          field={field}
          onChange={onChange}
          disabled={disabled}
          filter={filter}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.TAGS) {
      return (
        <TagCreate
          moduleAPIName={module?.APIName}
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.LOCATION) {
      return (
        <LocationCreate
          type={type}
          field={field}
          existingValue={value}
          inputSize={size}
          onChange={(val: any) => onChange(field, val)}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.CALCULATED) {
      return <CalculatedCreate field={field} />;
    }

    if (type === Constants.FIELDTYPES.FILTER) {
      return (
        <FilterCreate
          moduleAPIName={module?.APIName}
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          ref={inputRef}
        />
      );
    }

    if (type === Constants.FIELDTYPES.COLOR) {
      return (
        <ColorCreate existingValue={value} field={field} onChange={onChange} />
      );
    }

    return typeof value === "object" ? JSON.stringify(value) : value;
  }
);

export default InputFormatter;
